import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  ArrowRight,
  ArrowLeft,
  Wallet,
  UtensilsCrossed,
  Bus,
  Heart,
  Shield,
  Landmark,
  Calculator,
  Info,
  ChevronDown,
  ChevronUp,
  Plus,
  X,
  Banknote,
  CircleDot,
  Monitor,
  Home,
  GraduationCap,
  Settings2,
  Check,
} from 'lucide-react';const fmt = (n) => '₦' + Math.round(n).toLocaleString('en-NG');

function calculatePAYE(chargeableIncome) {
  if (chargeableIncome <= 0) return 0;
  const bands = [
    { limit: 800000, rate: 0.0 },
    { limit: 2200000, rate: 0.15 },
    { limit: 9000000, rate: 0.18 },
    { limit: 13000000, rate: 0.21 },
    { limit: 25000000, rate: 0.23 },
    { limit: Infinity, rate: 0.25 },
  ];
  let tax = 0;
  let remaining = chargeableIncome;
  for (const band of bands) {
    if (remaining <= 0) break;
    const taxable = Math.min(remaining, band.limit);
    tax += taxable * band.rate;
    remaining -= taxable;
  }
  return tax;
}

function compute(params) {
  const {
    gross, rent, food, transport, hmo, life, volPension, customBenefits,
    employerPensionRate, basicPct, housingPct, transportPct,
    workTools, housingBenefit, education,
  } = params;

  const monthlyGross = Math.max(0, gross);
  const annualRent = Math.max(0, rent);
  const pensionablePct = (basicPct + housingPct + transportPct) / 100;

  // Employer pension (from total package)
  const employerPensionMonthly = (employerPensionRate / 100) * pensionablePct * monthlyGross;

  // Total benefits (all non-cash, non-pension)
  const customTotal = customBenefits.reduce((s, b) => s + (b.amount || 0), 0);
  const totalBenefits = food + transport + hmo + life + workTools + housingBenefit + education + customTotal;

  // Cash salary = package minus employer pension and benefits
  const monthlyCash = Math.max(0, monthlyGross - employerPensionMonthly - totalBenefits);
  const annualCash = monthlyCash * 12;

  // Employee deductions (from cash salary)
  const employeePensionMonthly = 0.08 * pensionablePct * monthlyCash;

  // Annualize for PAYE
  const annualEmployeePension = employeePensionMonthly * 12;
  const rentRelief = annualRent > 0 ? Math.min(0.2 * annualRent, 500000) : 0;
  const lifeAnnual = life * 12;
  const volPensionAnnual = volPension * 12;

  // Housing BIK — taxable portion capped at 20% of gross
  const annualGross = monthlyGross * 12;
  const housingBIK = housingBenefit > 0 ? Math.min(housingBenefit * 12, 0.2 * annualGross) : 0;

  // Chargeable income
  const chargeableIncome = Math.max(0,
    annualCash
    - annualEmployeePension
    - rentRelief
    - lifeAnnual
    - volPensionAnnual
    + housingBIK
  );

  const annualPAYE = calculatePAYE(chargeableIncome);
  const monthlyPAYE = annualPAYE / 12;
  const monthlyTakeHome = Math.max(0, monthlyCash - employeePensionMonthly - monthlyPAYE);

  // Tax band breakdown
  const bands = [
    { limit: 800000, rate: 0.0, label: '₦800,000 @ 0%' },
    { limit: 2200000, rate: 0.15, label: '₦2,200,000 @ 15%' },
    { limit: 9000000, rate: 0.18, label: '₦9,000,000 @ 18%' },
    { limit: 13000000, rate: 0.21, label: '₦13,000,000 @ 21%' },
    { limit: 25000000, rate: 0.23, label: '₦25,000,000 @ 23%' },
    { limit: Infinity, rate: 0.25, label: 'Above ₦50,000,000 @ 25%' },
  ];
  const taxBands = [];
  let remaining = chargeableIncome;
  for (const band of bands) {
    if (remaining <= 0) break;
    const taxable = Math.min(remaining, band.limit);
    const tax = taxable * band.rate;
    taxBands.push({ label: band.label, tax });
    remaining -= taxable;
  }

  const employerPensionAnnual = employerPensionMonthly * 12;
  const totalBenefitsAnnual = totalBenefits * 12;

  return {
    monthlyGross, monthlyCash, totalBenefits, totalBenefitsAnnual,
    employerPensionMonthly, employerPensionAnnual,
    employeePensionMonthly, annualEmployeePension,
    monthlyPAYE, annualPAYE, monthlyTakeHome,
    annualCash, annualGross, rentRelief,
    lifeAnnual, volPensionAnnual, housingBIK,
    chargeableIncome, taxBands,
  };
}

const DELIVERY_OPTIONS = [
  { value: 'vendor', label: 'Provided through vendor', short: 'via vendor' },
  { value: 'company', label: 'Provided directly by company', short: 'provided by company' },
  { value: 'voucher', label: 'Voucher/card', short: 'via voucher' },
];

const EMPLOYER_PENSION_RATES = [10, 12, 15, 18, 20];

function CurrencyInput({ label, value, onChange, icon: Icon, iconColor = '#D64D7A', hint }) {
  const display = value === 0 ? '' : value.toLocaleString('en-NG');
  const handleChange = (e) => {
    const raw = e.target.value.replace(/[^0-9]/g, '');
    onChange(raw === '' ? 0 : parseInt(raw, 10));
  };
  return (
    <div>
      <label className="flex items-center gap-2 text-sm font-medium text-[#374151] mb-1.5">
        {Icon && (
          <div className="w-6 h-6 rounded-md flex items-center justify-center" style={{ backgroundColor: `${iconColor}15` }}>
            <Icon className="w-3.5 h-3.5" style={{ color: iconColor }} />
          </div>
        )}
        {label}
      </label>
      <div className="relative">
        <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-sm font-semibold text-[#9ca3af]">₦</span>
        <input
          type="text"
          inputMode="numeric"
          value={display}
          onChange={handleChange}
          placeholder="0"
          className="w-full h-11 pl-8 pr-4 rounded-lg border border-[#e5e7eb] bg-white text-[15px] font-semibold text-[#1a1d1f] focus:outline-none focus:ring-2 focus:ring-[#D64D7A]/30 focus:border-[#D64D7A] transition-all"
          style={{ fontFamily: 'JetBrains Mono, monospace' }}
          data-testid={`input-${label.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
        />
      </div>
      {hint && <p className="text-[11px] text-[#9ca3af] mt-1">{hint}</p>}
    </div>
  );
}

function FlowLine({ amount, label, sub, highlight, isLast, icon: Icon, color, animDelay = 0 }) {
  return (
    <div className="flex items-start gap-3 relative" style={{ animationDelay: `${animDelay}ms` }}>
      <div className="flex flex-col items-center flex-shrink-0 w-5">
        <div className={`w-2.5 h-2.5 rounded-full border-2 mt-1.5 ${highlight ? 'bg-[#00B894] border-[#00B894]' : 'bg-white border-[#d1d5db]'}`} />
        {!isLast && <div className="w-px flex-1 bg-[#e5e7eb] min-h-[28px]" />}
      </div>
      <div className={`flex items-start justify-between flex-1 min-w-0 ${isLast ? 'pb-0' : 'pb-3'}`}>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            {Icon && (
              <div className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${color}15` }}>
                <Icon className="w-3 h-3" style={{ color }} />
              </div>
            )}
            <span className={`text-sm ${highlight ? 'font-bold text-[#1a1d1f]' : 'text-[#374151]'}`}>{label}</span>
          </div>
          {sub && <p className="text-[11px] text-[#9ca3af] mt-0.5 ml-8">{sub}</p>}
        </div>
        <span
          className={`text-sm font-semibold ml-3 flex-shrink-0 ${highlight ? 'text-[#00B894] font-extrabold text-base' : 'text-[#1a1d1f]'}`}
          style={{ fontFamily: 'JetBrains Mono, monospace' }}
        >
          {fmt(amount)}
        </span>
      </div>
    </div>
  );
}

export default function BenefitsEstimator() {
  const navigate = useNavigate();

  // Core inputs
  const [gross, setGross] = useState(500000);
  const [rent, setRent] = useState(1200000);

  // Salary breakdown (advanced)
  const [basicPct, setBasicPct] = useState(40);
  const [housingPct, setHousingPct] = useState(20);
  const [transportPct, setTransportPct] = useState(10);
  const [showBreakdown, setShowBreakdown] = useState(false);

  // Pension
  const [employerPensionRate, setEmployerPensionRate] = useState(10);
  const [volPension, setVolPension] = useState(0);

  // Main benefits
  const [food, setFood] = useState(30000);
  const [transport, setTransport] = useState(20000);
  const [hmo, setHmo] = useState(25000);
  const [life, setLife] = useState(10000);

  // Extended benefits
  const [workTools, setWorkTools] = useState(0);
  const [housingBenefit, setHousingBenefit] = useState(0);
  const [education, setEducation] = useState(0);
  const [showMoreBenefits, setShowMoreBenefits] = useState(false);

  // Custom benefits
  const [customBenefits, setCustomBenefits] = useState([]);

  // UI
  const [showPAYE, setShowPAYE] = useState(false);

  const r = useMemo(() => compute({
    gross, rent, food, transport, hmo, life, volPension, customBenefits,
    employerPensionRate, basicPct, housingPct, transportPct,
    workTools, housingBenefit, education,
  }), [gross, rent, food, transport, hmo, life, volPension, customBenefits,
    employerPensionRate, basicPct, housingPct, transportPct,
    workTools, housingBenefit, education]);

  const customTotal = customBenefits.reduce((s, b) => s + (b.amount || 0), 0);
  const allBenefits = food + transport + hmo + life + workTools + housingBenefit + education + customTotal;
  const overBudget = allBenefits + r.employerPensionMonthly > gross;

  const addCustomBenefit = () => {
    if (customBenefits.length >= 10) return;
    setCustomBenefits([...customBenefits, { name: '', amount: 0, delivery: 'vendor' }]);
  };
  const updateCustom = (idx, field, val) => {
    const updated = [...customBenefits];
    updated[idx] = { ...updated[idx], [field]: val };
    setCustomBenefits(updated);
  };
  const removeCustom = (idx) => setCustomBenefits(customBenefits.filter((_, i) => i !== idx));
  const getDeliveryShort = (val) => DELIVERY_OPTIONS.find((d) => d.value === val)?.short || '';

  const totalPensionMonthly = r.employerPensionMonthly + r.employeePensionMonthly;

  // Build flow lines (ordered per spec)
  const flowLines = [];
  // 1. Bank account
  flowLines.push({ amount: r.monthlyTakeHome, label: 'Your bank account', highlight: true, icon: Banknote, color: '#00B894' });
  // 2. Pension lines
  flowLines.push({ amount: r.employerPensionMonthly, label: 'Your pension fund', sub: `company contribution (${employerPensionRate}%)`, icon: Landmark, color: '#6366f1' });
  flowLines.push({ amount: r.employeePensionMonthly, label: 'Your pension fund', sub: 'your contribution (8%)', icon: Landmark, color: '#6366f1' });
  // 3. Company-provided benefits
  if (food > 0) flowLines.push({ amount: food, label: 'Meals', sub: 'via company vendor', icon: UtensilsCrossed, color: '#f59e0b' });
  if (transport > 0) flowLines.push({ amount: transport, label: 'Transport', sub: 'via company arrangement', icon: Bus, color: '#ec4899' });
  if (hmo > 0) flowLines.push({ amount: hmo, label: 'Health cover', sub: 'HMO provider', icon: Heart, color: '#00B894' });
  if (life > 0) flowLines.push({ amount: life, label: 'Life insurance', sub: 'insurance provider', icon: Shield, color: '#8b5cf6' });
  if (workTools > 0) flowLines.push({ amount: workTools, label: 'Work tools / device', sub: 'provided by company', icon: Monitor, color: '#0ea5e9' });
  if (housingBenefit > 0) flowLines.push({ amount: housingBenefit, label: 'Housing', sub: 'provided by company', icon: Home, color: '#f97316' });
  if (education > 0) flowLines.push({ amount: education, label: 'Training / education', sub: 'provided by company', icon: GraduationCap, color: '#14b8a6' });
  if (volPension > 0) flowLines.push({ amount: volPension, label: 'Voluntary pension', sub: 'additional contribution', icon: Landmark, color: '#6366f1' });
  customBenefits.forEach((cb) => {
    if (cb.amount > 0) flowLines.push({ amount: cb.amount, label: cb.name || 'Custom benefit', sub: getDeliveryShort(cb.delivery), icon: CircleDot, color: '#D64D7A' });
  });
  // 5. PAYE
  flowLines.push({ amount: r.monthlyPAYE, label: 'PAYE tax', icon: Wallet, color: '#9ca3af' });

  return (
    <div className="min-h-screen bg-[#f9fafb]" data-testid="estimator-page">
      {/* Nav */}
      <nav className="bg-white border-b border-[#f0f0f0] sticky top-0 z-50" data-testid="estimator-nav">
        <div className="max-w-5xl mx-auto flex items-center justify-between h-[56px] px-5 lg:px-8">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 group" data-testid="estimator-logo">
            <img src="/pebbles-logo.svg" alt="Pebbles" className="h-7 group-hover:scale-105 transition-transform" />
          </button>
          <div className="flex items-center gap-2.5">
            <Button variant="outline" className="rounded-lg text-[13px] font-semibold border-[#e5e7eb] h-9 px-4" onClick={() => navigate('/login')} data-testid="estimator-login-btn">Log In</Button>
            <Button className="rounded-lg text-[13px] font-semibold bg-[#D64D7A] hover:bg-[#c4406b] text-white h-9 px-4" onClick={() => navigate('/login')} data-testid="estimator-get-started-btn">Get Started</Button>
          </div>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-5 lg:px-8 py-8 lg:py-12">
        <button onClick={() => navigate('/')} className="flex items-center gap-1.5 text-sm text-[#6b7280] hover:text-[#1a1d1f] mb-6 transition-colors" data-testid="back-to-home">
          <ArrowLeft className="w-4 h-4" /> Back to home
        </button>

        {/* Header */}
        <div className="mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#D64D7A]/10 text-[#D64D7A] text-xs font-bold uppercase tracking-wider mb-4">
            <Calculator className="w-3.5 h-3.5" /> Benefits Estimator
          </div>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-[#1a1d1f] tracking-tight mb-3" style={{ fontFamily: 'Manrope' }} data-testid="estimator-headline">
            See what your compensation looks like
          </h1>
          <p className="text-base text-[#6b7280] max-w-2xl">
            See how your compensation breaks down when your company provides benefits directly, through vendors, vouchers, and providers.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* LEFT: Inputs */}
          <div className="space-y-5" data-testid="inputs-section">

            {/* Card 1: Pay & Benefits + Rent + Salary Breakdown */}
            <div className="bg-white rounded-xl border border-[#e5e7eb] p-6">
              <h2 className="text-sm font-bold text-[#1a1d1f] mb-4" style={{ fontFamily: 'Manrope' }}>Your pay & benefits</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <CurrencyInput label="Your Monthly Pay & Benefits Package" value={gross} onChange={setGross} icon={Wallet} iconColor="#D64D7A" hint="The total amount your company pays in salary and benefits each month" />
                <CurrencyInput label="Annual Rent Paid" value={rent} onChange={setRent} icon={Landmark} iconColor="#6366f1" hint="For rent relief calculation (NTA 2025)" />
              </div>

              {/* Salary Breakdown Toggle */}
              <button
                onClick={() => setShowBreakdown(!showBreakdown)}
                className="flex items-center gap-2 mt-4 text-sm text-[#6b7280] hover:text-[#1a1d1f] transition-colors w-full"
                data-testid="salary-breakdown-toggle"
              >
                <Settings2 className="w-3.5 h-3.5" />
                <span>Customise salary breakdown</span>
                <span className="text-[11px] text-[#9ca3af] ml-1">(Basic {basicPct}% | Housing {housingPct}% | Transport {transportPct}%)</span>
                <span className="ml-auto">{showBreakdown ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}</span>
              </button>
              {showBreakdown && (
                <div className="mt-4 pt-4 border-t border-[#f0f0f0]">
                  <p className="text-[11px] text-[#9ca3af] mb-3 leading-relaxed">
                    These determine your pensionable emoluments (Basic + Housing + Transport) and affect pension calculations. If you don't know your breakdown, the defaults work for most companies.
                  </p>
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { label: 'Basic', value: basicPct, set: setBasicPct, testId: 'basic-pct' },
                      { label: 'Housing', value: housingPct, set: setHousingPct, testId: 'housing-pct' },
                      { label: 'Transport', value: transportPct, set: setTransportPct, testId: 'transport-pct' },
                    ].map((f) => (
                      <div key={f.label}>
                        <label className="text-[11px] font-medium text-[#6b7280] mb-1 block">{f.label} %</label>
                        <div className="relative">
                          <input
                            type="number"
                            min={0}
                            max={100}
                            value={f.value}
                            onChange={(e) => f.set(Math.max(0, Math.min(100, parseInt(e.target.value) || 0)))}
                            className="w-full h-10 px-3 pr-8 rounded-lg border border-[#e5e7eb] bg-white text-sm font-semibold text-[#1a1d1f] focus:outline-none focus:ring-2 focus:ring-[#D64D7A]/30 focus:border-[#D64D7A]"
                            style={{ fontFamily: 'JetBrains Mono, monospace' }}
                            data-testid={f.testId}
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#9ca3af]">%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-[11px] text-[#9ca3af] mt-2">Pensionable: {basicPct + housingPct + transportPct}% of cash salary</p>
                </div>
              )}
            </div>

            {/* Card 2: Pension */}
            <div className="bg-white rounded-xl border border-[#e5e7eb] p-6">
              <h2 className="text-sm font-bold text-[#1a1d1f] mb-4" style={{ fontFamily: 'Manrope' }}>Pension</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-[#374151] mb-1.5">
                    <div className="w-6 h-6 rounded-md flex items-center justify-center bg-[#6366f1]/10">
                      <Landmark className="w-3.5 h-3.5 text-[#6366f1]" />
                    </div>
                    Employer Pension Rate
                  </label>
                  <select
                    value={employerPensionRate}
                    onChange={(e) => setEmployerPensionRate(parseInt(e.target.value))}
                    className="w-full h-11 px-3 rounded-lg border border-[#e5e7eb] bg-white text-[15px] font-semibold text-[#1a1d1f] focus:outline-none focus:ring-2 focus:ring-[#D64D7A]/30 focus:border-[#D64D7A] appearance-none"
                    style={{ fontFamily: 'JetBrains Mono, monospace' }}
                    data-testid="employer-pension-rate"
                  >
                    {EMPLOYER_PENSION_RATES.map((rate) => (
                      <option key={rate} value={rate}>{rate}%</option>
                    ))}
                  </select>
                  <p className="text-[11px] text-[#9ca3af] mt-1">Company's contribution to your PFA (min. 10%)</p>
                </div>
                <CurrencyInput label="Additional Voluntary Pension" value={volPension} onChange={setVolPension} icon={Landmark} iconColor="#6366f1" hint="Extra pension above mandatory 8%, deductible from taxable income" />
              </div>
            </div>

            {/* Card 3: Company-Provided Benefits */}
            <div className="bg-white rounded-xl border border-[#e5e7eb] p-6">
              <div className="flex items-center justify-between mb-1">
                <h2 className="text-sm font-bold text-[#1a1d1f]" style={{ fontFamily: 'Manrope' }}>Company-provided benefits</h2>
                {overBudget && (
                  <span className="text-xs font-semibold text-red-500 bg-red-50 px-2 py-0.5 rounded-full" data-testid="over-budget-warning">Exceeds total package</span>
                )}
              </div>
              <p className="text-[12px] text-[#9ca3af] mb-5">
                These are benefits your company provides directly, delivered through approved vendors and providers, not as cash.
              </p>

              <div className="grid sm:grid-cols-2 gap-4">
                <CurrencyInput label="Food Benefit" value={food} onChange={setFood} icon={UtensilsCrossed} iconColor="#f59e0b" hint="Meals provided through company vendors or vouchers" />
                <CurrencyInput label="Transport Benefit" value={transport} onChange={setTransport} icon={Bus} iconColor="#ec4899" hint="Transport provided through company arrangements" />
                <CurrencyInput label="Health Cover (HMO)" value={hmo} onChange={setHmo} icon={Heart} iconColor="#00B894" hint="Health insurance premium (HMO provider)" />
                <CurrencyInput label="Life Insurance" value={life} onChange={setLife} icon={Shield} iconColor="#8b5cf6" hint="Life insurance premium (insurance provider, deductible)" />
              </div>

              {/* Show More Benefits */}
              <button onClick={() => setShowMoreBenefits(!showMoreBenefits)} className="flex items-center gap-1.5 text-sm text-[#6b7280] hover:text-[#1a1d1f] mt-4 transition-colors" data-testid="show-more-benefits-toggle">
                {showMoreBenefits ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                {showMoreBenefits ? 'Hide' : 'Show more'} benefits
              </button>
              {showMoreBenefits && (
                <div className="mt-4 pt-4 border-t border-[#f0f0f0] grid sm:grid-cols-2 gap-4">
                  <CurrencyInput label="Work Tools / Device" value={workTools} onChange={setWorkTools} icon={Monitor} iconColor="#0ea5e9" hint="Laptops, phones, equipment provided for work. Generally non-taxable when required for the role." />
                  <div>
                    <CurrencyInput label="Housing Benefit" value={housingBenefit} onChange={setHousingBenefit} icon={Home} iconColor="#f97316" hint="Company-provided accommodation." />
                    {housingBenefit > 0 && (
                      <p className="text-[11px] text-amber-600 mt-1 bg-amber-50 px-2 py-1 rounded" data-testid="housing-bik-note">
                        Housing benefits are treated differently. A portion may be taxable (capped at 20% of gross). The estimator applies this cap.
                      </p>
                    )}
                  </div>
                  <CurrencyInput label="Education / Training" value={education} onChange={setEducation} icon={GraduationCap} iconColor="#14b8a6" hint="Employer-funded training or education for the employee." />
                </div>
              )}

              {/* Custom Benefits */}
              {customBenefits.length > 0 && (
                <div className="mt-5 pt-5 border-t border-[#f0f0f0] space-y-4" data-testid="custom-benefits-section">
                  <p className="text-xs font-bold text-[#6b7280] uppercase tracking-wider">Additional Benefits</p>
                  {customBenefits.map((cb, idx) => (
                    <div key={idx} className="bg-[#f9fafb] rounded-lg p-4 relative" data-testid={`custom-benefit-${idx}`}>
                      <button
                        onClick={() => removeCustom(idx)}
                        className="absolute top-3 right-3 w-6 h-6 rounded-full bg-white border border-[#e5e7eb] flex items-center justify-center text-[#9ca3af] hover:text-red-500 hover:border-red-200 transition-colors"
                        data-testid={`remove-custom-${idx}`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                      <div className="grid sm:grid-cols-3 gap-3">
                        <div>
                          <label className="text-[11px] font-medium text-[#6b7280] mb-1 block">Benefit Name</label>
                          <input type="text" value={cb.name} onChange={(e) => updateCustom(idx, 'name', e.target.value)} placeholder="e.g. Staff housing, Gym"
                            className="w-full h-10 px-3 rounded-lg border border-[#e5e7eb] bg-white text-sm text-[#1a1d1f] focus:outline-none focus:ring-2 focus:ring-[#D64D7A]/30 focus:border-[#D64D7A]"
                            data-testid={`custom-name-${idx}`}
                          />
                        </div>
                        <div>
                          <label className="text-[11px] font-medium text-[#6b7280] mb-1 block">Amount (monthly)</label>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-[#9ca3af]">₦</span>
                            <input type="text" inputMode="numeric"
                              value={cb.amount === 0 ? '' : cb.amount.toLocaleString('en-NG')}
                              onChange={(e) => { const raw = e.target.value.replace(/[^0-9]/g, ''); updateCustom(idx, 'amount', raw === '' ? 0 : parseInt(raw, 10)); }}
                              placeholder="0"
                              className="w-full h-10 pl-7 pr-3 rounded-lg border border-[#e5e7eb] bg-white text-sm font-semibold text-[#1a1d1f] focus:outline-none focus:ring-2 focus:ring-[#D64D7A]/30 focus:border-[#D64D7A]"
                              style={{ fontFamily: 'JetBrains Mono, monospace' }}
                              data-testid={`custom-amount-${idx}`}
                            />
                          </div>
                        </div>
                        <div>
                          <label className="text-[11px] font-medium text-[#6b7280] mb-1 block">Delivery method</label>
                          <select value={cb.delivery} onChange={(e) => updateCustom(idx, 'delivery', e.target.value)}
                            className="w-full h-10 px-3 rounded-lg border border-[#e5e7eb] bg-white text-sm text-[#1a1d1f] focus:outline-none focus:ring-2 focus:ring-[#D64D7A]/30 focus:border-[#D64D7A] appearance-none"
                            data-testid={`custom-delivery-${idx}`}
                          >
                            {DELIVERY_OPTIONS.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
                          </select>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {customBenefits.length < 10 && (
                <button onClick={addCustomBenefit} className="flex items-center gap-2 mt-4 text-sm font-medium text-[#D64D7A] hover:text-[#c4406b] transition-colors" data-testid="add-custom-benefit">
                  <Plus className="w-4 h-4" /> Add another benefit
                </button>
              )}
            </div>

            {/* Summary bar */}
            <div className={`bg-white rounded-xl border border-[#e5e7eb] p-5 ${overBudget ? 'border-red-200 bg-red-50/30' : ''}`}>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-[#374151]">Benefits Provided by Company</span>
                  <span className="text-sm font-extrabold" style={{ fontFamily: 'JetBrains Mono, monospace' }} data-testid="total-benefits">{fmt(allBenefits)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-[#374151]">Employer Pension</span>
                  <span className="text-sm font-extrabold text-[#6366f1]" style={{ fontFamily: 'JetBrains Mono, monospace' }} data-testid="employer-pension-display">{fmt(r.employerPensionMonthly)}</span>
                </div>
                <div className="flex items-center justify-between pt-1.5 border-t border-[#f0f0f0]">
                  <span className="text-sm font-semibold text-[#374151]">Cash Salary</span>
                  <span className="text-sm font-extrabold text-[#D64D7A]" style={{ fontFamily: 'JetBrains Mono, monospace' }} data-testid="cash-salary">{fmt(r.monthlyCash)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT: Flow Visual */}
          <div className="space-y-6" data-testid="output-section">
            <div className="bg-white rounded-xl border border-[#e5e7eb] p-6 lg:p-8">
              <h2 className="text-xl sm:text-2xl font-extrabold text-[#1a1d1f] mb-1 tracking-tight" style={{ fontFamily: 'Manrope' }} data-testid="flow-title">
                Where Your {fmt(r.monthlyGross)} in Pay &amp; Benefits Package Goes
              </h2>
              <p className="text-[12px] text-[#9ca3af] mb-6">Every naira, accounted for.</p>

              <div className="bg-[#1a1d1f] rounded-xl p-5 mb-6">
                <p className="text-[11px] text-[#9ca3af] uppercase tracking-wider font-bold mb-1">Pay & Benefits Package</p>
                <p className="text-2xl sm:text-3xl font-extrabold text-white" style={{ fontFamily: 'Manrope' }} data-testid="gross-display">{fmt(r.monthlyGross)}</p>
              </div>

              <div className="pl-1" data-testid="flow-lines">
                {flowLines.map((line, i) => (
                  <FlowLine key={i} amount={line.amount} label={line.label} sub={line.sub} highlight={line.highlight}
                    isLast={i === flowLines.length - 1} icon={line.icon} color={line.color} animDelay={i * 60} />
                ))}
              </div>

              {/* Pension total note */}
              {totalPensionMonthly > 0 && (
                <p className="text-[11px] text-[#6366f1] mt-3 ml-8" style={{ fontFamily: 'JetBrains Mono, monospace' }} data-testid="pension-total-note">
                  Total to your PFA: {fmt(totalPensionMonthly + volPension)}/mo
                </p>
              )}

              <div className="mt-5 pt-4 border-t-2 border-[#1a1d1f] flex items-center justify-between">
                <span className="text-sm font-extrabold text-[#1a1d1f]">Every naira accounted for.</span>
                <span className="text-base font-extrabold text-[#1a1d1f]" style={{ fontFamily: 'JetBrains Mono, monospace' }} data-testid="flow-total">{fmt(r.monthlyGross)}</span>
              </div>
            </div>

            {/* Expandable PAYE Breakdown */}
            <div className="bg-white rounded-xl border border-[#e5e7eb]" data-testid="paye-breakdown-section">
              <button onClick={() => setShowPAYE(!showPAYE)} className="w-full flex items-center gap-2 px-5 py-4 text-sm text-[#6b7280] hover:text-[#1a1d1f] transition-colors" data-testid="paye-breakdown-toggle">
                {showPAYE ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                How was your PAYE calculated?
              </button>
              {showPAYE && (
                <div className="px-5 pb-5 border-t border-[#f0f0f0]" data-testid="paye-breakdown-content">
                  <p className="text-xs text-[#6b7280] mt-4 mb-3">Your annual PAYE is based on:</p>
                  <div className="space-y-1.5" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    <div className="flex justify-between text-xs text-[#374151]">
                      <span>Total pay & benefits package</span>
                      <span>{fmt(r.annualGross)}</span>
                    </div>
                    <div className="flex justify-between text-xs text-[#6b7280]">
                      <span>Less: Employer pension ({employerPensionRate}%)</span>
                      <span>-{fmt(r.employerPensionAnnual)}</span>
                    </div>
                    {r.totalBenefitsAnnual > 0 && (
                      <div className="flex justify-between text-xs text-[#6b7280]">
                        <span>Less: Company-provided benefits</span>
                        <span>-{fmt(r.totalBenefitsAnnual)}</span>
                      </div>
                    )}
                    <div className="border-t border-[#e5e7eb] pt-1.5 flex justify-between text-xs font-semibold text-[#374151]">
                      <span>Cash salary (annual)</span>
                      <span>{fmt(r.annualCash)}</span>
                    </div>
                    <div className="flex justify-between text-xs text-[#6b7280]">
                      <span>Less: Employee pension (8%)</span>
                      <span>-{fmt(r.annualEmployeePension)}</span>
                    </div>
                    {r.rentRelief > 0 && (
                      <div className="flex justify-between text-xs text-[#6b7280]">
                        <span>Less: Rent relief</span>
                        <span>-{fmt(r.rentRelief)}</span>
                      </div>
                    )}
                    {r.lifeAnnual > 0 && (
                      <div className="flex justify-between text-xs text-[#6b7280]">
                        <span>Less: Life insurance premium</span>
                        <span>-{fmt(r.lifeAnnual)}</span>
                      </div>
                    )}
                    {volPension > 0 && (
                      <div className="flex justify-between text-xs text-[#6b7280]">
                        <span>Less: Voluntary pension</span>
                        <span>-{fmt(r.volPensionAnnual)}</span>
                      </div>
                    )}
                    {r.housingBIK > 0 && (
                      <div className="flex justify-between text-xs text-amber-600">
                        <span>Plus: Housing benefit-in-kind (20% cap)</span>
                        <span>+{fmt(r.housingBIK)}</span>
                      </div>
                    )}
                    <div className="border-t border-[#e5e7eb] pt-1.5 flex justify-between text-xs font-semibold text-[#1a1d1f]">
                      <span>Chargeable income</span>
                      <span>{fmt(r.chargeableIncome)}</span>
                    </div>
                  </div>

                  <p className="text-xs text-[#6b7280] mt-5 mb-3">Tax bands applied (NTA 2025):</p>
                  <div className="space-y-1.5" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    {r.taxBands.map((band, i) => (
                      <div key={i} className="flex justify-between text-xs text-[#6b7280]">
                        <span>{band.label}</span>
                        <span>{fmt(band.tax)}</span>
                      </div>
                    ))}
                    <div className="border-t border-[#e5e7eb] pt-1.5 flex justify-between text-xs font-semibold text-[#1a1d1f]">
                      <span>Annual PAYE</span>
                      <span>{fmt(r.annualPAYE)}</span>
                    </div>
                    <div className="flex justify-between text-xs font-bold text-[#D64D7A]">
                      <span>Monthly PAYE</span>
                      <span>{fmt(r.monthlyPAYE)}</span>
                    </div>
                  </div>

                  {/* Pension summary */}
                  <div className="mt-5 pt-3 border-t border-[#f0f0f0]">
                    <p className="text-xs text-[#6b7280] mb-1.5">Total annual pension (to your PFA):</p>
                    <div className="flex gap-4 text-xs" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                      <span className="text-[#6b7280]">Company: {fmt(r.employerPensionAnnual)}</span>
                      <span className="text-[#6b7280]">+ You: {fmt(r.annualEmployeePension + (volPension * 12))}</span>
                      <span className="font-semibold text-[#6366f1]">= {fmt(r.employerPensionAnnual + r.annualEmployeePension + (volPension * 12))}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Disclaimer */}
            <div className="bg-white rounded-xl border border-[#e5e7eb] p-5" data-testid="disclaimer">
              <div className="flex gap-2.5">
                <Info className="w-4 h-4 text-[#9ca3af] flex-shrink-0 mt-0.5" />
                <p className="text-[11px] text-[#9ca3af] leading-relaxed">
                  This estimator provides approximate figures for illustration purposes only.
                  Actual tax treatment depends on employer configuration, documentation, and
                  compliance with the Nigeria Tax Act 2025. Benefits shown represent goods
                  and services provided directly by the employer through approved vendors
                  and providers, not cash allowances. Tax treatment depends on the form of
                  delivery. Pebbles does not provide tax advice. Consult a qualified tax
                  professional for specific guidance.
                </p>
              </div>
            </div>

            {/* CTA */}
            <div className="bg-[#1a1d1f] rounded-xl p-6 text-center" data-testid="estimator-cta">
              <p className="text-sm font-semibold text-white mb-1" style={{ fontFamily: 'Manrope' }}>Want to set this up for your team?</p>
              <p className="text-[12px] text-[#9ca3af] mb-4">Give your team real benefits, delivered through vendors, not just deposited as cash.</p>
              <div className="flex flex-col sm:flex-row gap-2.5 justify-center">
                <Button className="bg-[#D64D7A] hover:bg-[#c4406b] text-white rounded-lg text-sm font-semibold h-10 px-5" onClick={() => navigate('/login')} data-testid="cta-get-started">
                  Get Started <ArrowRight className="ml-1.5 w-4 h-4" />
                </Button>
                <Button variant="outline" className="border-white/20 text-white hover:bg-white/10 rounded-lg text-sm font-semibold h-10 px-5" onClick={() => navigate('/login')} data-testid="cta-talk-to-us">
                  Talk to Us
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
